import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { ICreateResponsibilityEntryPayload } from '@/modules/tasks/types/ICreateResponsibilityEntryPayload'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'
import type { TResponsibilitySourceType } from '@/modules/tasks/types/IResponsibilityListParent'
import type { ITaskChecklist, ITaskChecklistItem } from '@/modules/tasks/types/ITaskChecklist'
import type { ITaskObserver } from '@/modules/tasks/types/ITaskObserver'
import type { ITasksFilters } from '@/modules/tasks/types/ITasksFilters'
import type { ITasksStoreState } from '@/modules/tasks/types/ITasksStoreState'
import { toApiDate } from '@/utils/formatters'
import { isHttpNotFound } from '@/utils/httpErrors'
import axios from 'axios'
import { defineStore } from 'pinia'

function createDefaultFilters(sourceTypes: TResponsibilitySourceType[] = []): ITasksFilters {
  return {
    search: '',
    source_types: sourceTypes,
    is_coordinator_entry: null,
    status: [],
    type: [],
    supervisors: [],
    performers: [],
    numbers: [],
    created_after: null,
    created_before: null,
    is_active: null,
    is_high_priority: null,
    is_overdue: null,
    requires_action: null,
    involvement: [],
    is_pinned: null,
  }
}

const inFlightEntryRequests = new Map<number, Promise<(IResponsibilityListItem & { instructions?: string }) | null>>()
const inFlightChecklistRequests = new Map<number, Promise<ITaskChecklist[]>>()
const inFlightObserverRequests = new Map<number, Promise<ITaskObserver[]>>()

export const useTasksStore = defineStore('tasks', {
  state: (): ITasksStoreState => ({
    entries: [],
    currentEntry: null,
    nextCursor: null,
    totalCount: 0,
    itemsPerPage: 25,
    filters: createDefaultFilters(),
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
    currentSortString: null,
  }),

  getters: {
    hasActiveFilters(state): boolean {
      const f = state.filters
      if (!f) return false
      if (f.search && f.search.trim() !== '') return true
      if (f.created_after !== null || f.created_before !== null) return true
      if (f.is_active !== null && f.is_active !== undefined) return true
      if (f.is_high_priority !== null && f.is_high_priority !== undefined) return true
      if (f.is_overdue !== null && f.is_overdue !== undefined) return true
      if (f.requires_action !== null && f.requires_action !== undefined) return true
      if (f.is_coordinator_entry !== null && f.is_coordinator_entry !== undefined) return true
      if (f.is_pinned !== null && f.is_pinned !== undefined) return true
      if (Array.isArray(f.status) && f.status.length > 0) return true
      if (Array.isArray(f.type) && f.type.length > 0) return true
      if (Array.isArray(f.supervisors) && f.supervisors.length > 0) return true
      if (Array.isArray(f.performers) && f.performers.length > 0) return true
      if (Array.isArray(f.numbers) && f.numbers.length > 0) return true
      if (Array.isArray(f.involvement) && f.involvement.length > 0) return true
      if (Array.isArray(f.source_types) && f.source_types.length > 0) return true
      return false
    },
  },

  actions: {
    resetFilters(sourceTypes?: TResponsibilitySourceType[]) {
      this.filters = createDefaultFilters(sourceTypes ?? this.filters.source_types)
    },

    setSourceType(sourceType: TResponsibilitySourceType) {
      this.filters.source_types = [sourceType]
    },

    /**
     * GET /api/responsibility/responsibility-entries/filter-choices/
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        if (!search && !cursor) {
          if (fieldKey === 'involvement') {
            const roles = await this.fetchRoleFilterChoices()
            if (roles && roles.length > 0) {
              this.filterChoices[fieldKey] = roles.map(r => ({ id: r.id, label: r.name, name: r.name, count: r.count }))
              return
            }
          } else if (fieldKey === 'source_types') {
            const sources = await this.fetchSourceFilterChoices(true)
            if (sources && sources.length > 0) {
              this.filterChoices[fieldKey] = sources.map(s => ({ id: s.id, label: s.name, name: s.name, count: s.count, is_present: s.is_present }))
              return
            }
          }
        }

        const params: Record<string, any> = { field: fieldKey }
        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.filters

        if (fieldKey !== 'status' && filters.status?.length)
          params.status = filters.status.join(',')
        if (fieldKey !== 'type' && filters.type?.length)
          params.type = filters.type.join(',')
        if (fieldKey !== 'supervisors' && filters.supervisors?.length)
          params.supervisors = filters.supervisors.join(',')
        if (fieldKey !== 'performers' && filters.performers?.length)
          params.performers = filters.performers.join(',')
        if (fieldKey !== 'numbers' && filters.numbers?.length)
          params.numbers = filters.numbers.join(',')
        if (fieldKey !== 'source_types' && filters.source_types?.length)
          params.source_types = filters.source_types.join(',')
        if (fieldKey !== 'involvement' && filters.involvement?.length)
          params.involvement = filters.involvement.join(',')
        if (fieldKey !== 'is_pinned' && filters.is_pinned !== null)
          params.is_pinned = filters.is_pinned
        if (fieldKey !== 'is_active' && filters.is_active !== null)
          params.is_active = filters.is_active
        if (fieldKey !== 'is_high_priority' && filters.is_high_priority !== null)
          params.is_high_priority = filters.is_high_priority
        if (fieldKey !== 'is_overdue' && filters.is_overdue !== null)
          params.is_overdue = filters.is_overdue
        if (fieldKey !== 'is_coordinator_entry' && filters.is_coordinator_entry !== null)
          params.is_coordinator_entry = filters.is_coordinator_entry
        if (fieldKey !== 'created_after' && filters.created_after)
          params.created_after = toApiDate(filters.created_after)
        if (fieldKey !== 'created_before' && filters.created_before)
          params.created_before = toApiDate(filters.created_before)
        if (filters.search)
          params.search = filters.search

        const response = await axios.get(
          `${BASE_URL}/api/responsibility/responsibility-entries/filter-choices/`,
          { params },
        )
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }
        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров задач [${fieldKey}]:`, e)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(e, 'Не удалось загрузить фильтры'),
          time: 7000,
        })
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /**
     * GET /api/responsibility/responsibility-entries/roles/
     * Возвращает список ролей с готовым подсчётом count
     */
    async fetchRoleFilterChoices(): Promise<Array<{ id: string; name: string; count: number }>> {
      try {
        const res = await axios.get(`${BASE_URL}/api/responsibility/responsibility-entries/roles/`)
        return res.data ?? []
      } catch (e) {
        console.error('Ошибка загрузки ролей задач:', e)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(e, 'Не удалось загрузить роли задач'),
          time: 5000,
        })
        return []
      }
    },

    /**
     * GET /api/responsibility/responsibility-entries/sources/?present=true
     * Возвращает список оснований задач с готовым подсчётом count
     */
    async fetchSourceFilterChoices(onlyPresent = true): Promise<Array<{ id: string; name: string; count: number; is_present?: boolean }>> {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/responsibility/responsibility-entries/sources/`,
          { params: onlyPresent ? { present: 'true' } : {} },
        )
        return res.data ?? []
      } catch (e) {
        console.error('Ошибка загрузки оснований задач:', e)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(e, 'Не удалось загрузить основания задач'),
          time: 5000,
        })
        return []
      }
    },

    async createEntry(payload: ICreateResponsibilityEntryPayload | FormData): Promise<IResponsibilityListItem | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData
        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/`,
          payload,
          isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
        )
        feedbackStore.showToast({
          type: 'success',
          message: 'Задача создана',
          time: 7000,
        })
        const created = Array.isArray(res.data) ? (res.data[0] || null) : res.data
        return created || null
      } catch (error) {
        console.error(error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать задачу'),
          time: 7000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async updateEntry(
      item: IResponsibilityListItem,
      payload: {
        responsible_employee_ids: string[]
        instructions: string
        deadline_time: string | null
        is_high_priority: boolean
      },
    ): Promise<boolean> {
      if (!item.id) return false
      const url = `${BASE_URL}/api/responsibility/responsibility-entries/${item.id}/`

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.patch(url, payload)
        feedbackStore.showToast({
          type: 'success',
          message: 'Задача обновлена',
          time: 4000,
        })
        return true
      } catch (error) {
        console.error(error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить задачу'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async deleteEntry(item: IResponsibilityListItem): Promise<boolean> {
      if (!item.id) return false
      const url = `${BASE_URL}/api/responsibility/responsibility-entries/${item.id}/`

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(url)
        feedbackStore.showToast({
          type: 'success',
          message: 'Задача удалена',
          time: 4000,
        })
        return true
      } catch (error) {
        console.error(error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить задачу'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async fetchEntries(cursor?: string, itemsPerPage?: number, throwOnError = false): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (this.entries.length === 0) {
        feedbackStore.isGlobalLoading = true
      }
      try {
        const params: Record<string, any> = {}

        if (this.currentSortString)
          params.sort = this.currentSortString

        if (itemsPerPage)
          this.itemsPerPage = itemsPerPage

        if (this.itemsPerPage)
          params.page_size = this.itemsPerPage

        if (cursor)
          params.cursor = cursor

        const filters = this.filters

        if (filters.search)
          params.search = filters.search
        if (filters.source_types?.length) {
          params.source_types = filters.source_types.join(',')
          params.sources = filters.source_types.join(',')
        }
        if (filters.involvement?.length) {
          params.involvement = filters.involvement.join(',')
          params.roles = filters.involvement.join(',')
        }
        if (filters.is_pinned !== null && filters.is_pinned !== undefined)
          params.is_pinned = filters.is_pinned
        if (filters.status?.length)
          params.status = filters.status.join(',')
        if (filters.type?.length)
          params.type = filters.type.join(',')
        if (filters.supervisors?.length)
          params.supervisors = filters.supervisors.join(',')
        if (filters.performers?.length)
          params.performers = filters.performers.join(',')
        if (filters.numbers?.length)
          params.numbers = filters.numbers.join(',')
        if (filters.is_active !== null)
          params.is_active = filters.is_active
        if (filters.is_high_priority !== null)
          params.is_high_priority = filters.is_high_priority
        if (filters.is_overdue !== null)
          params.is_overdue = filters.is_overdue
        if (filters.requires_action !== null && filters.requires_action !== undefined)
          params.requires_action = filters.requires_action
        if (filters.is_coordinator_entry !== null)
          params.is_coordinator_entry = filters.is_coordinator_entry
        if (filters.created_after)
          params.created_after = toApiDate(filters.created_after)
        if (filters.created_before)
          params.created_before = toApiDate(filters.created_before)

        const res = await axios.get(`${BASE_URL}/api/responsibility/responsibility-entries/`, { params })
        const rawResults: IResponsibilityListItem[] = res.data.results ?? res.data
        const results = Array.isArray(rawResults) ? rawResults : []

        this.nextCursor = res.data.next ?? null
        this.entries = cursor ? [...this.entries, ...results] : results
        this.totalCount = res.data.count ?? this.entries.length
      } catch (error) {
        if (throwOnError) throw error
        console.error(`❌ Ошибка загрузки задач: ${String(error)}`)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить задачи'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async fetchEntryById(id: number): Promise<(IResponsibilityListItem & { instructions?: string }) | null> {
      if (inFlightEntryRequests.has(id)) {
        return inFlightEntryRequests.get(id)!
      }

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      const request = (async () => {
        try {
          const res = await axios.get(
            `${BASE_URL}/api/responsibility/responsibility-entries/${id}/`,
          )
          const data = res.data ?? null
          if (data && Array.isArray(data.reports) && data.reports.length > 0) {
            const lastItem = data.reports[data.reports.length - 1]
            if (lastItem?.report && !data.last_report) {
              data.last_report = lastItem.report
            }
          }
          return data
        } catch (error) {
          console.error(error)
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить задачу'),
            time: 7000,
          })
          if (isHttpNotFound(error))
            throw error
          return null
        } finally {
          inFlightEntryRequests.delete(id)
          feedbackStore.isGlobalLoading = false
        }
      })()

      inFlightEntryRequests.set(id, request)
      return request
    },

    async applyResponsibilityEntryRealtime(payload: IRealtimePayload): Promise<void> {
      if (!payload?.event_type || !payload?.data) return

      const data = payload.data as IResponsibilityListItem & { id?: number }
      if (data.id == null) return

      const feedbackStore = useFeedbackStore()
      const currentOpenId = this.currentEntry?.id ?? feedbackStore.globalResponsibilityEntryId
      const isOpen = Boolean(currentOpenId && currentOpenId === data.id)
      const idx = this.entries.findIndex(item => item.id === data.id)

      // Проверяем, является ли эта запись подзадачей открытой сейчас задачи
      const parentId = typeof data.parent_entry === 'object' && data.parent_entry ? data.parent_entry.id : Number(data.parent_entry)
      const isSubtaskOfCurrent = Boolean(
        this.currentEntry && (
          (parentId && this.currentEntry.id === parentId) ||
          (Array.isArray(this.currentEntry.subtasks) && this.currentEntry.subtasks.some(s => s.id === data.id))
        )
      )

      if (payload.event_type === 'responsibility_entry_deleted') {
        if (isOpen) {
          feedbackStore.showToast({
            type: 'warning',
            message: 'Открытая задача была удалена другим сотрудником',
            time: 7000,
          })
          this.currentEntry = null
          feedbackStore.globalResponsibilityEntryId = null
        }
        if (idx >= 0) {
          this.entries.splice(idx, 1)
          this.totalCount = Math.max(0, this.totalCount - 1)
        }
        if (parentId) {
          const parentEntryInList = this.entries.find(e => e.id === parentId)
          if (parentEntryInList && Array.isArray(parentEntryInList.subtasks)) {
            const sIdx = parentEntryInList.subtasks.findIndex(s => s.id === data.id)
            if (sIdx >= 0) {
              parentEntryInList.subtasks.splice(sIdx, 1)
              parentEntryInList.subtasks = [...parentEntryInList.subtasks]
              parentEntryInList.subtasks_count = parentEntryInList.subtasks.length
              parentEntryInList.completed_subtasks_count = parentEntryInList.subtasks.filter(s => s.status === 'completed').length
            }
          }
        }
        if (isSubtaskOfCurrent && this.currentEntry && Array.isArray(this.currentEntry.subtasks)) {
          const sIdx = this.currentEntry.subtasks.findIndex(s => s.id === data.id)
          if (sIdx >= 0) {
            this.currentEntry.subtasks.splice(sIdx, 1)
            this.currentEntry.subtasks = [...this.currentEntry.subtasks]
            this.currentEntry = { ...this.currentEntry }
          }
        }
        window.dispatchEvent(new CustomEvent('responsibility-entry-realtime', { detail: payload }))
        return
      }

      // 1. Обновляем локальный массив реестра (только для корневых задач, подзадачи не добавляем в entries)
      switch (payload.event_type) {
        case 'responsibility_entry_created':
          if (idx >= 0) {
            this.entries.splice(idx, 1, { ...this.entries[idx], ...data })
          } else if (!parentId) {
            this.entries = [data, ...this.entries]
            this.totalCount++
          }
          break

        case 'responsibility_entry_updated':
          if (idx >= 0) {
            this.entries.splice(idx, 1, { ...this.entries[idx], ...data })
          } else if (!this.hasActiveFilters && !parentId) {
            this.entries = [data, ...this.entries]
            this.totalCount++
          }
          break
      }

      // 2. Если обновленная запись — подзадача: обновляем её в массиве subtasks родительской задачи в реестре
      if (parentId) {
        const parentEntryInList = this.entries.find(e => e.id === parentId)
        if (parentEntryInList && Array.isArray(parentEntryInList.subtasks)) {
          const sIdx = parentEntryInList.subtasks.findIndex(s => s.id === data.id)
          if (sIdx >= 0) {
            parentEntryInList.subtasks.splice(sIdx, 1, {
              ...parentEntryInList.subtasks[sIdx],
              ...data,
            })
          } else if (payload.event_type === 'responsibility_entry_created') {
            parentEntryInList.subtasks = [data, ...parentEntryInList.subtasks]
          }
          parentEntryInList.subtasks = [...parentEntryInList.subtasks]
          parentEntryInList.subtasks_count = parentEntryInList.subtasks.length
          parentEntryInList.completed_subtasks_count = parentEntryInList.subtasks.filter(s => s.status === 'completed').length
        }
      }

      // При активных фильтрах в открытом реестре перезапрашиваем актуальный список с сервера (только для корневых задач)
      if (!parentId && this.hasActiveFilters && this.entries.length > 0) {
        await this.fetchEntries(undefined, undefined, false)
      }

      // 2. Если обновленная запись — подзадача открытой сейчас задачи: обновляем её статус в subtasks
      if (isSubtaskOfCurrent && this.currentEntry) {
        if (Array.isArray(this.currentEntry.subtasks)) {
          const sIdx = this.currentEntry.subtasks.findIndex(s => s.id === data.id)
          if (sIdx >= 0) {
            this.currentEntry.subtasks.splice(sIdx, 1, {
              ...this.currentEntry.subtasks[sIdx],
              ...data,
            })
          } else if (payload.event_type === 'responsibility_entry_created') {
            this.currentEntry.subtasks = [data, ...this.currentEntry.subtasks]
          }
          this.currentEntry.subtasks = [...this.currentEntry.subtasks]
          this.currentEntry = { ...this.currentEntry }
        }

        // В фоне актуализируем полные детали родительской задачи
        const currentParentId = this.currentEntry?.id
        if (typeof currentParentId === 'number') {
          const updatedParent = await this.fetchEntryById(currentParentId)
          if (updatedParent) {
            this.currentEntry = updatedParent
          }
        }
      }

      // 3. Если сама открытая задача прямо сейчас в фокусе — синхронизируем её полные детали
      let fullDetails: (IResponsibilityListItem & { instructions?: string; checklists?: ITaskChecklist[] }) | null = null
      if (isOpen) {
        fullDetails = await this.fetchEntryById(data.id)
        if (fullDetails) {
          this.currentEntry = fullDetails
        }
      }

      // Оповещаем все активные компоненты (TaskCard и др.) о событии Centrifugo
      window.dispatchEvent(
        new CustomEvent('responsibility-entry-realtime', {
          detail: {
            ...payload,
            fullDetails,
          },
        }),
      )
    },

    /**
     * Закрепление / открепление задачи
     */
    async togglePin(entryId: number, isCurrentlyPinned: boolean): Promise<boolean> {
      try {
        const url = `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/pin/`
        const res = isCurrentlyPinned
          ? await axios.delete(url)
          : await axios.post(url)

        const newPinnedState = res.data?.is_pinned !== undefined
          ? Boolean(res.data.is_pinned)
          : !isCurrentlyPinned

        const entry = this.entries.find(e => e.id === entryId)
        if (entry) {
          entry.is_pinned = newPinnedState
        }

        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'success',
          message: newPinnedState ? 'Задача закреплена' : 'Задача откреплена',
        })

        return newPinnedState
      } catch (error) {
        console.error('Ошибка переключения закрепления задачи:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить закрепление задачи'),
          time: 5000,
        })
        return isCurrentlyPinned
      }
    },

    /**
     * Чек-листы
     */
    async fetchChecklists(entryId: number): Promise<ITaskChecklist[]> {
      if (inFlightChecklistRequests.has(entryId)) {
        return inFlightChecklistRequests.get(entryId)!
      }

      const request = (async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/`)
          return res.data ?? []
        } catch (error: any) {
          if (error?.response?.status === 404) {
            return []
          }
          console.error('Ошибка загрузки чек-листов:', error)
          const feedbackStore = useFeedbackStore()
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить чек-листы'),
            time: 5000,
          })
          return []
        } finally {
          inFlightChecklistRequests.delete(entryId)
        }
      })()

      inFlightChecklistRequests.set(entryId, request)
      return request
    },

    async createChecklist(entryId: number, title?: string): Promise<ITaskChecklist | null> {
      try {
        const res = await axios.post(`${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/`, {
          title: title || 'Чек-лист',
        })
        return res.data
      } catch (error) {
        console.error('Ошибка создания чек-листа:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать чек-лист'),
          time: 5000,
        })
        return null
      }
    },

    async deleteChecklist(entryId: number, checklistId: number): Promise<boolean> {
      try {
        await axios.delete(`${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/${checklistId}/`)
        return true
      } catch (error) {
        console.error('Ошибка удаления чек-листа:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить чек-лист'),
          time: 5000,
        })
        return false
      }
    },

    async createChecklistItem(entryId: number, checklistId: number, text: string): Promise<ITaskChecklistItem | null> {
      try {
        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/${checklistId}/items/`,
          { text },
        )
        return res.data
      } catch (error) {
        console.error('Ошибка добавления пункта чек-листа:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось добавить пункт'),
          time: 5000,
        })
        return null
      }
    },

    async updateChecklistItem(
      entryId: number,
      checklistId: number,
      itemId: number,
      text: string,
    ): Promise<ITaskChecklistItem | null> {
      try {
        const res = await axios.patch(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/${checklistId}/items/${itemId}/`,
          { text },
        )
        return res.data
      } catch (error) {
        console.error('Ошибка обновления пункта чек-листа:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить пункт'),
          time: 5000,
        })
        return null
      }
    },

    async toggleChecklistItem(entryId: number, checklistId: number, itemId: number): Promise<ITaskChecklistItem | null> {
      try {
        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/${checklistId}/items/${itemId}/toggle/`,
        )
        return res.data
      } catch (error) {
        console.error('Ошибка переключения пункта чек-листа:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить статус пункта'),
          time: 5000,
        })
        return null
      }
    },

    async deleteChecklistItem(entryId: number, checklistId: number, itemId: number): Promise<boolean> {
      try {
        await axios.delete(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/checklists/${checklistId}/items/${itemId}/`,
        )
        return true
      } catch (error) {
        console.error('Ошибка удаления пункта чек-листа:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить пункт'),
          time: 5000,
        })
        return false
      }
    },

    /**
     * Наблюдатели
     */
    async fetchObservers(entryId: number): Promise<ITaskObserver[]> {
      if (inFlightObserverRequests.has(entryId)) {
        return inFlightObserverRequests.get(entryId)!
      }

      const request = (async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/observers/`)
          return res.data ?? []
        } catch (error: any) {
          if (error?.response?.status === 404) {
            return []
          }
          console.error('Ошибка загрузки наблюдателей:', error)
          const feedbackStore = useFeedbackStore()
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить наблюдателей'),
            time: 5000,
          })
          return []
        } finally {
          inFlightObserverRequests.delete(entryId)
        }
      })()

      inFlightObserverRequests.set(entryId, request)
      return request
    },

    async addObservers(entryId: number, employeeIds: string[]): Promise<ITaskObserver[]> {
      try {
        const res = await axios.post(`${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/observers/`, {
          employee_ids: employeeIds,
        })
        return res.data ?? []
      } catch (error) {
        console.error('Ошибка добавления наблюдателей:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось добавить наблюдателей'),
          time: 5000,
        })
        return []
      }
    },

    async removeObservers(entryId: number, employeeIds: string[]): Promise<ITaskObserver[]> {
      try {
        const res = await axios.delete(`${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/observers/`, {
          data: { employee_ids: employeeIds },
        })
        return res.data ?? []
      } catch (error) {
        console.error('Ошибка удаления наблюдателей:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить наблюдателей'),
          time: 5000,
        })
        return []
      }
    },

    /**
     * Создание подзадачи
     */
    async createSubtask(parentEntryId: number, payload: FormData | Record<string, any>): Promise<IResponsibilityListItem | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/${parentEntryId}/subtasks/`,
          payload,
        )
        feedbackStore.showToast({
          type: 'success',
          message: 'Подзадача создана',
          time: 5000,
        })
        return Array.isArray(res.data) ? (res.data[0] || null) : res.data
      } catch (error) {
        console.error('Ошибка создания подзадачи:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать подзадачу'),
          time: 7000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Точечное обновление деталей задачи (заголовок, описание, срок, приоритет)
     */
    async updateTaskDetails(entryId: number, payload: Record<string, any>): Promise<IResponsibilityListItem | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const body = { ...payload }
        if (body.responsible_employee_id && !body.responsible_employee_ids) {
          body.responsible_employee_ids = [body.responsible_employee_id]
        }
        const res = await axios.patch(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/`,
          body,
        )
        const idx = this.entries.findIndex(e => e.id === entryId)
        if (idx >= 0 && res.data) {
          this.entries[idx] = { ...this.entries[idx], ...res.data }
        }
        if (this.currentEntry && this.currentEntry.id === entryId && res.data) {
          this.currentEntry = { ...this.currentEntry, ...res.data }
        }
        return res.data
      } catch (error) {
        console.error('Ошибка обновления задачи:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить изменения'),
          time: 5000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка файлов к существующей задаче через FormData
     */
    async uploadTaskFiles(entryId: number, files: File[]): Promise<IResponsibilityListItem | null> {
      if (!files || files.length === 0) return null
      const feedbackStore = useFeedbackStore()
      try {
        const formData = new FormData()
        for (const f of files) {
          formData.append('files', f)
        }
        const res = await axios.patch(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        const idx = this.entries.findIndex(e => e.id === entryId)
        if (idx >= 0 && res.data) {
          this.entries[idx] = { ...this.entries[idx], ...res.data }
        }
        feedbackStore.showToast({
          type: 'success',
          message: 'Файлы успешно добавлены',
          time: 3000,
        })
        return res.data
      } catch (error) {
        console.error('Ошибка загрузки файлов к задаче:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить файлы'),
          time: 5000,
        })
        return null
      }
    },


    /**
     * Переключение срочности (важности) задачи
     */
    async togglePriority(item: IResponsibilityListItem): Promise<boolean> {
      if (!item.id) return false
      const newPriority = !item.is_high_priority
      const updated = await this.updateTaskDetails(item.id, { is_high_priority: newPriority })
      if (updated) {
        item.is_high_priority = updated.is_high_priority
        return Boolean(updated.is_high_priority)
      }
      return Boolean(item.is_high_priority)
    },

    /**
     * Взятие задачи в работу через бэкенд-экшен POST /api/responsibility/responsibility-entries/{id}/in-progress/
     */
    async takeInProgress(entryId: number): Promise<IResponsibilityListItem | null> {
      const feedbackStore = useFeedbackStore()
      try {
        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/in-progress/`,
        )
        const idx = this.entries.findIndex(e => e.id === entryId)
        if (idx >= 0 && res.data) {
          this.entries[idx] = { ...this.entries[idx], ...res.data }
        }
        feedbackStore.showToast({
          type: 'success',
          message: 'Задача взята в работу',
          time: 4000,
        })
        return res.data
      } catch (error) {
        console.error('Ошибка взятия задачи в работу:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось взять задачу в работу'),
          time: 5000,
        })
        return null
      }
    },

    /**
     * Возврат завершённой задачи в работу через бэкенд-экшен POST /api/responsibility/responsibility-entries/{id}/return/
     */
    async returnInProgress(entryId: number): Promise<IResponsibilityListItem | null> {
      const feedbackStore = useFeedbackStore()
      try {
        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/return/`,
        )
        const idx = this.entries.findIndex(e => e.id === entryId)
        if (idx >= 0 && res.data) {
          this.entries[idx] = { ...this.entries[idx], ...res.data }
        }
        feedbackStore.showToast({
          type: 'success',
          message: 'Задача возвращена в работу',
          time: 4000,
        })
        return res.data
      } catch (error) {
        console.error('Ошибка возврата задачи в работу:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось вернуть задачу в работу'),
          time: 5000,
        })
        return null
      }
    },

    /**
     * Завершение задачи с отчетом через бэкенд-экшен POST /api/responsibility/responsibility-entries/{id}/complete/
     */
    async completeTask(entryId: number, report: string, files?: File[]): Promise<IResponsibilityListItem | null> {
      const feedbackStore = useFeedbackStore()
      try {
        let payload: any
        let headers: Record<string, string> | undefined

        if (files && files.length > 0) {
          const formData = new FormData()
          formData.append('report', report)
          for (const f of files) {
            formData.append('files', f)
          }
          payload = formData
          headers = { 'Content-Type': 'multipart/form-data' }
        } else {
          payload = { report }
        }

        const res = await axios.post(
          `${BASE_URL}/api/responsibility/responsibility-entries/${entryId}/complete/`,
          payload,
          { headers },
        )

        const idx = this.entries.findIndex(e => e.id === entryId)
        if (idx >= 0 && res.data) {
          const lastReportText = res.data.reports?.[res.data.reports.length - 1]?.report
            || res.data.last_report
            || res.data.report
            || report
          this.entries[idx] = {
            ...this.entries[idx],
            ...res.data,
            last_report: lastReportText,
            requires_action: false,
          }
        }
        feedbackStore.showToast({
          type: 'success',
          message: 'Задача завершена',
          time: 4000,
        })
        return res.data
      } catch (error) {
        console.error('Ошибка завершения задачи:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось завершить задачу'),
          time: 5000,
        })
        return null
      }
    },

    updateRequiresAction(id: number, requiresAction: boolean) {
      for (const entry of this.entries) {
        if (Number(entry.id) === id) {
          entry.requires_action = requiresAction
        }
        if (Array.isArray(entry.subtasks)) {
          const sub = entry.subtasks.find(s => Number(s.id) === id)
          if (sub) {
            sub.requires_action = requiresAction
          }
        }
      }
      if (this.currentEntry && Number(this.currentEntry.id) === id) {
        this.currentEntry.requires_action = requiresAction
      }
      if (this.currentEntry && Array.isArray(this.currentEntry.subtasks)) {
        const sub = this.currentEntry.subtasks.find(s => Number(s.id) === id)
        if (sub) {
          sub.requires_action = requiresAction
        }
      }
    },
  },
})
